/**
 * The shared "we are working, here is what on" panel for the GBP surface.
 *
 * Two waits run back to back on a client switch — the page-level listing
 * lookup (~3s), then the audit itself (8-35s). They used to look nothing
 * alike: a bare antd spinner, then a different block. Swapping between two
 * treatments mid-wait reads as something going wrong, so both now render
 * through here.
 *
 * There is deliberately no elapsed clock. It was here, sitting outside the
 * announced region so a screen reader was not told the time every second, but
 * a ticking counter turns a wait into something to watch: it invites you to
 * measure the tool rather than read what it is telling you. The staged copy
 * already shows the wait is alive. Removed 2026-09-23.
 */
import { LoadingAnimation } from "@/components/ui/LoadingAnimation";

import type { GbpLoadingStage } from "./gbpAuditLoadingCopy";

interface Props {
	stage: GbpLoadingStage;
	/** Distinguishes the two waits in tests. */
	testId?: string;
}

export default function GbpWaitState({ stage, testId = "gbp-wait" }: Props) {
	return (
		<div
			className="flex flex-col items-center gap-3 py-10 text-center"
			role="status"
			aria-live="polite"
			data-testid={testId}
		>
			<LoadingAnimation size="lg" />
			<div className="space-y-1">
				<p
					className={
						stage.tone === "warning"
							? "text-sm font-medium text-destructive"
							: "text-sm font-medium"
					}
					data-testid={`${testId}-headline`}
				>
					{stage.headline}
				</p>
				<p className="mx-auto max-w-md text-xs text-muted-foreground">
					{stage.sub}
				</p>
			</div>
		</div>
	);
}
